/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS CINCO DOCUMENTOS LEGALES · cuáles son, cómo se llaman, cuáles se aceptan
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D32, preguntas del dueño que son la especificación entera:
 *
 *     «¿ya creaste una categoría en el superadmin para poder leer los contratos
 *      y editarlos, en los tres idiomas? ¿además estos contratos según el
 *      idioma son los que aparecen en el registro para la persona marcar el
 *      checkbox, diciendo "acepto el contrato de servicios"? ¿pusiste también
 *      una categoría dentro del superadmin, para poder editar los términos de
 *      servicios, que van en la landing, en el footer?»
 *
 * Aquí está lo que NO cambia sin desplegar: cuáles son los cinco, cómo se
 * llaman en cada idioma, y cuáles hay que aceptar para crear una cuenta. El
 * TEXTO de cada uno vive en la base y lo edita el dueño desde el superadmin,
 * que es la mitad de lo que pidió.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTOS CINCO
 *
 * El dueño dijo «los cinco» sin enumerarlos, así que los elegí yo y queda
 * apuntado en `DECISIONES-PENDIENTES` para que pueda cambiar cualquiera:
 *
 *   · `terminos`    el contrato de servicios. Es el del checkbox del registro.
 *   · `privacidad`  qué datos se guardan y cuáles no. Aquí pesa más que en casi
 *                   cualquier producto: la gente contesta preguntas sobre su
 *                   salud ANTES de tener cuenta.
 *   · `reembolso`   cómo se cancela y qué pasa con lo pagado.
 *   · `alcance`     qué es esto y qué NO es. La regla del dueño —no se nombra
 *                   una condición, no se diagnostica, no se sustituye a nadie—
 *                   ya está en el pie de todas las pantallas; aquí es un
 *                   documento entero porque es lo que más protege a las dos
 *                   partes.
 *   · `contacto`    quién está detrás y cómo se le escribe. Antes del 05/09 no
 *                   había NI UNA mención de contacto en toda la plataforma.
 *
 * ⚠ Y NINGUNO LLEVA PLAZO DE DEVOLUCIÓN ESCRITO todavía. `D32` dice que la base
 * de la empresa es Brasil —CDC art. 49, siete días— y la dirección que dio el
 * dueño el 05/09 es de Bogotá, con una SAS colombiana. Son dos leyes distintas
 * y dos plazos distintos. Está abierto en `DECISIONES-PENDIENTES §H1` y hasta
 * que lo diga no se escribe ningún número de días en ninguna parte.
 */

import type { Idioma } from "./idiomas.ts";

/**
 * Los cinco, EN EL ORDEN EN QUE SE ENSEÑAN.
 *
 * El orden no es alfabético: es el de importancia para quien llega. Los
 * términos y la privacidad son los que se aceptan; el alcance es el que dice
 * qué es esto; el contacto va al final porque se busca cuando ya se sabe todo
 * lo demás.
 *
 * ⚠ Esta lista tiene que coincidir con el enum `documento_legal_tipo` de la
 * migración 011. Lo comprueba `verificar:legales` por las dos caras.
 */
export const LEGALES = ["terminos", "privacidad", "reembolso", "alcance", "contacto"] as const;

export type TipoLegal = (typeof LEGALES)[number];

export function esTipoLegal(valor: unknown): valor is TipoLegal {
  return typeof valor === "string" && (LEGALES as readonly string[]).includes(valor);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS QUE HAY QUE ACEPTAR PARA CREAR UNA CUENTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño escribió la casilla en singular —«acepto el contrato de
 * servicios»— y son DOS los que se aceptan, no uno. El porqué:
 *
 * Este producto pregunta por la salud de quien lo usa **antes de que tenga
 * cuenta**, y guarda esas respuestas. Aceptar solo el contrato de servicios
 * dejaría eso sin cubrir, que es justo la parte que más protección necesita en
 * los tres mercados donde se vende.
 *
 * Es UNA casilla, como él pidió, con los dos documentos enlazados dentro. Queda
 * apuntado en `DECISIONES-PENDIENTES §H2` por si prefiere exactamente su frase
 * y solo el contrato.
 */
export const LEGALES_QUE_SE_ACEPTAN: readonly TipoLegal[] = ["terminos", "privacidad"];

/**
 * Cómo se llama cada uno en cada idioma.
 *
 * NO va en el diccionario de la interfaz y no es un descuido: estos nombres son
 * el TÍTULO de un documento, y el título del documento lo escribe el dueño en
 * el superadmin junto con su cuerpo. Lo de aquí es el nombre del ENLACE —lo que
 * se lee en el pie de la página de venta— que tiene que existir aunque el
 * documento todavía no esté publicado.
 *
 * Se guardan aquí y no en `@vc/i18n` porque el pie de la página de venta y el
 * superadmin los necesitan los dos, y el superadmin no carga el diccionario de
 * la plataforma.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Y LLEVAN LA PALABRA «POLÍTICA» DELANTE, QUE NO ES RELLENO
 *
 * La casilla del registro dice «Acepto el {a} y la {b}», y con `privacidad`
 * llamándose solo «Privacidad» salía «Acepto el Contrato de servicios y la
 * Privacidad», que no se lee en ninguno de los tres idiomas. Con «Política de
 * privacidad» la frase encaja en los tres y el enlace sigue diciendo lo mismo.
 *
 * Es la clase de detalle que solo se ve pintándolo: la cadena estaba bien, la
 * traducción estaba bien, y la frase montada no.
 */
export const NOMBRE_DEL_LEGAL: Readonly<Record<Idioma, Readonly<Record<TipoLegal, string>>>> = {
  es: {
    terminos: "Contrato de servicios",
    privacidad: "Política de privacidad",
    reembolso: "Cancelación y devoluciones",
    alcance: "Qué es esto y qué no",
    contacto: "Contacto",
  },
  en: {
    terminos: "Terms of service",
    privacidad: "Privacy policy",
    reembolso: "Cancellation and refunds",
    alcance: "What this is and what it is not",
    contacto: "Contact",
  },
  pt: {
    terminos: "Contrato de serviços",
    privacidad: "Política de privacidade",
    reembolso: "Cancelamento e devoluções",
    alcance: "O que isto é e o que não é",
    contacto: "Contato",
  },
};

/**
 * El camino público de cada documento. `/terminos`, `/privacidad`…
 *
 * En castellano para los tres idiomas, y a propósito: es la misma decisión que
 * `/app` y `/confirmar`. Un camino que cambia con el idioma son tres URLs para
 * el mismo documento, tres cosas que indexar y tres que enlazar mal. El idioma
 * va en `?idioma=`, como en la página de venta.
 */
export function caminoDelLegal(tipo: TipoLegal): string {
  return `/${tipo}`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA MARCA DEL BORRADOR, Y POR QUÉ VIVE AQUÍ Y NO EN EL GUION QUE LA ESCRIBE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `sembrar-legales.ts` empieza los quince borradores con un aviso que dice que
 * los escribió el equipo de desarrollo y no un abogado. La idea era que quien
 * fuera a publicar lo leyera y lo quitara.
 *
 * No funcionó ni en desarrollo. Al mirar la base el 05/09 había **seis
 * documentos publicados con el aviso dentro**, y no cualquiera: `terminos` y
 * `privacidad` en los tres idiomas, o sea justo los dos que hay que ACEPTAR
 * para registrarse. Los publicó una prueba, sin leer nada, porque publicar es
 * un botón y el aviso es texto.
 *
 * En desarrollo da igual. El día que eso pase con la base de producción, el
 * contrato que acepta la gente empieza diciendo que no lo ha visto un abogado.
 *
 * Así que la marca deja de ser un recado y pasa a ser una CONDICIÓN: la ruta
 * de publicar mira si el cuerpo la lleva y se niega. Para que las dos partes
 * miren la misma cadena, vive aquí, que es lo que las dos pueden importar.
 *
 * Se comprueban los TRES idiomas contra CUALQUIER cuerpo, no cada idioma
 * contra el suyo: quien copia y pega el borrador castellano dentro del inglés
 * está haciendo exactamente lo que esto quiere impedir.
 */
export const MARCA_DE_BORRADOR: readonly string[] = [
  "⚠ BORRADOR SIN REVISAR",
  "⚠ UNREVIEWED DRAFT",
  "⚠ RASCUNHO SEM REVISÃO",
];

/** Si el texto todavía se declara borrador, cuál de las marcas lleva. */
export function laMarcaDeBorradorQueLleva(cuerpo: string): string | null {
  return MARCA_DE_BORRADOR.find((m) => cuerpo.includes(m)) ?? null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Y LOS HUECOS SIN RELLENAR, QUE SON MÁS FÁCILES DE PUBLICAR SIN VERLOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La marca de borrador va en la PRIMERA LÍNEA y se ve al abrir el documento.
 * Un hueco no: `[PENDIENTE: razón social]` vive en mitad de un texto de mil
 * caracteres, en la cláusula que nadie relee porque «esa parte ya estaba».
 *
 * Y hoy hay tres huecos de esos esperando —la razón social, el número fiscal y
 * la forma jurídica— porque solo los sabe el dueño y los va a rellenar él desde
 * el superadmin. O sea que el escenario no es hipotético: es el siguiente.
 *
 * Publicar un contrato que dice «[PENDIENTE: razón social] presta el servicio»
 * es peor que publicar uno con el aviso de borrador arriba, porque el aviso al
 * menos avisa. Aquí la persona firma con una empresa que no tiene nombre.
 *
 * `verificar:legales` ya exige lo otro —que un corchete lleve PENDIENTE dentro,
 * para que se vea al leer— así que las dos reglas se sostienen: los huecos se
 * marcan para verse, y lo marcado no se publica.
 */
export const MARCAS_DE_HUECO: readonly string[] = ["PENDIENTE", "PENDING", "PENDENTE"];

/** El primer hueco sin rellenar que queda en el texto, tal como está escrito. */
export function elHuecoSinRellenar(cuerpo: string): string | null {
  for (const trozo of cuerpo.match(/\[[^\]]*\]/g) ?? []) {
    if (MARCAS_DE_HUECO.some((m) => trozo.includes(m))) return trozo;
  }
  return null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PONER AL DÍA LO PUBLICADO QUE NO HA TOCADO NADIE (16/09/2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Palabras del dueño, al decirle que la política de privacidad publicada en su
 * base todavía hablaba de «tu contraseña cifrada» (se entra con un código desde
 * el 15/09) y que había que volver a publicarla desde el panel:
 *
 *     «tienes que tener todos los contratos en la DB y con acceso de edición
 *      por el superadmin. luego ya no es más suyo, deja todo publicado que el
 *      abogado entra a revisar antes del primer cliente»
 *
 * Desde el 13/09 el arranque publica lo que no tiene ninguna versión. Lo que no
 * hacía es seguir al código: sembrar es `ON CONFLICT DO NOTHING`, así que un
 * cambio del texto en el repositorio no llegaba nunca a una base ya sembrada.
 *
 * Esta función decide cuándo SÍ se puede publicar el texto del código encima, y
 * la regla es la de no pisar a nadie: solo mientras ninguna persona haya tocado
 * ese documento. En cuanto el abogado guarde o publique desde el panel, el
 * documento es suyo y el código ya no lo cambia nunca.
 *
 * Recibe los cuerpos YA SIN el párrafo del aviso de borrador: el publicado nunca
 * lo lleva y el borrador sí, y compararlos con él dentro los daría siempre por
 * distintos.
 */
export function sePuedePonerAlDia(d: {
  /** La última versión publicada la firmó una persona (`publicado_por` no nulo). */
  readonly publicadoPorAlguien: boolean;
  /** El borrador lo guardó una persona desde el panel (`actualizado_por` no nulo). */
  readonly borradorTocadoPorAlguien: boolean;
  readonly borradorSinAviso: string;
  readonly publicado: string;
  readonly delCodigoSinAviso: string;
}): boolean {
  if (d.publicadoPorAlguien || d.borradorTocadoPorAlguien) return false;
  /*
    El borrador y lo publicado tienen que decir lo mismo: si no, alguien cambió
    uno de los dos por otro camino, y eso tampoco se pisa.
  */
  if (d.borradorSinAviso.trim() !== d.publicado.trim()) return false;
  return d.publicado.trim() !== d.delCodigoSinAviso.trim();
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA GARANTIA, ESCRITA UNA VEZ · «catorce» estaba en seis sitios sueltos
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La palabra vive en SEIS textos escritos a mano: la promesa de la pagina de
 * venta en tres idiomas, y el parrafo del plazo en los cinco contratos, otras
 * tres. Ninguna cadena sabia de las otras.
 *
 * Y esa desconexion ya se noto caminando el producto el 06/09/2026: la pagina
 * prometia CATORCE dias por WhatsApp y el contrato publicado que se leia al
 * pulsar «Cancelacion y devoluciones» decia SIETE por correo. Era otra cosa
 * —una base sembrada antes del cambio— pero enseno la forma del fallo: un
 * comprador que compara las dos frases encuentra dos promesas distintas sobre
 * cuanto tiempo tiene para arrepentirse, y esa es de las peores en las que
 * puede pillarte alguien.
 *
 * Aqui esta el numero una vez. `verificar:legales` comprueba que la palabra
 * salga en los seis sitios, asi que cambiar el plazo en uno y olvidar los
 * otros cinco deja de ser posible en silencio.
 *
 * ── POR QUE UNA PALABRA Y NO UNA CIFRA ────────────────────────────────────
 *
 * Porque los textos estan escritos en prosa —«dentro de los catorce primeros
 * dias»— y ahi un «14» se lee como un formulario. La palabra es la que hay que
 * comprobar porque la palabra es la que se lee.
 */
export const DIAS_DE_GARANTIA = 14;

/**
 * El plazo del articulo 49 del CDC brasileno, que es un DERECHO y no una
 * promesa nuestra. Se nombra junto a la garantia a proposito: son dos plazos
 * distintos y el texto explica cual es cual.
 */
export const DIAS_DEL_DERECHO = 7;

/**
 * Los numeros escritos con letra, para poder leer un plazo en prosa.
 *
 * ── POR QUE UNA TABLA Y NO BUSCAR «catorce» ───────────────────────────────
 *
 * La primera version de la comprobacion buscaba la palabra dentro del texto, y
 * SE QUEDO MUDA en su propia prueba: se cambio «dentro de los catorce primeros
 * dias» por «treinta» en la pagina de venta, y siguio en verde — porque el
 * titulo del bloque de al lado seguia diciendo «Catorce dias para
 * arrepentirte». Buscar una palabra en un texto largo es casi no buscar.
 *
 * Con la tabla se puede leer al reves: sacar TODOS los plazos que el texto
 * nombra —«<numero> dias»— y exigir que no haya ninguno que no sea de la casa.
 * Un «treinta dias» nuevo aparece como un plazo desconocido, aunque el catorce
 * siga estando tres lineas mas arriba.
 *
 * Solo estan los numeros con los que se escribe un plazo. Uno que falte se
 * nota: aparece como texto y no como plazo, y entonces se anade aqui.
 */
export const NUMEROS_EN_PALABRAS: Readonly<Record<Idioma, Readonly<Record<string, number>>>> = {
  es: {
    tres: 3, cinco: 5, siete: 7, diez: 10, catorce: 14, quince: 15,
    treinta: 30, sesenta: 60, noventa: 90,
  },
  en: {
    three: 3, five: 5, seven: 7, ten: 10, fourteen: 14, fifteen: 15,
    thirty: 30, sixty: 60, ninety: 90,
  },
  pt: {
    tres: 3, cinco: 5, sete: 7, dez: 10, catorze: 14, quinze: 15,
    trinta: 30, sessenta: 60, noventa: 90,
  },
};

/** Como se dice «dias» en cada idioma, para anclar la busqueda del plazo. */
export const LA_PALABRA_DIAS: Readonly<Record<Idioma, string>> = {
  es: "dias",
  en: "days",
  pt: "dias",
};

/**
 * Todos los plazos EN DIAS que nombra un texto, leidos de su prosa.
 *
 * Se busca «<numero en palabras> dias», que es como se escriben en los
 * contratos y en la pagina de venta. Sin tildes y en minusculas, porque el
 * mismo texto los escribe con mayuscula en los titulares.
 */
export function losPlazosQueNombra(texto: string, idioma: Idioma): readonly number[] {
  const limpio = texto.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  const dias = LA_PALABRA_DIAS[idioma];
  const encontrados: number[] = [];
  for (const [palabra, n] of Object.entries(NUMEROS_EN_PALABRAS[idioma])) {
    /* `\b` a los dos lados: sin el, «sete» casaria dentro de «setenta» y
       «ten» dentro de «often». Y el hueco entre el numero y «dias» admite
       cualquier cosa corta —«catorce primeros dias»— pero no una frase. */
    const patron = new RegExp(`\\b${palabra}\\b[\\p{L} ]{0,12}?\\b${dias}\\b`, "u");
    if (patron.test(limpio)) encontrados.push(n);
  }
  return encontrados;
}
